/**
 * JD Analysis — Phase 1 of the Strategic Alignment Engine
 *
 * Pipeline:
 *   extractJDStructure(jobData)  →  JDStructure  (structured JD data)
 *   analyzeGaps(resumeText, jd)  →  GapAnalysis  (per-skill buckets + weak bullets)
 *
 * No LLM calls here — pure heuristics + keyword matching.
 * The gap analysis result is passed into buildOptimizationPrompt so the LLM
 * receives targeted, surgical instructions instead of "rewrite everything."
 */

import type { JobData } from '../types/optimization';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface JDStructure {
  title: string;
  company: string;
  requiredSkills: string[];
  preferredSkills: string[];
  responsibilities: string[];
  domainKeywords: string[];
  softSkills: string[];
  tools: string[];
  seniority: 'Junior' | 'Mid-Level' | 'Senior' | 'Executive' | 'Unknown';
}

/** Which resume-vs-JD bucket a required skill falls into */
export type GapBucket = 'strong' | 'weak' | 'implied' | 'missing';

export interface SkillGapItem {
  skill: string;
  bucket: GapBucket;
  /** Snippet from the resume that provides evidence (for strong/weak/implied) */
  context?: string;
}

export interface WeakBullet {
  text: string;
  /** Human-readable explanation of why it is weak */
  reason: string;
  /** JD requirements this bullet should better reflect */
  relatedJDRequirements: string[];
}

export interface GapAnalysis {
  strongMatches: SkillGapItem[];
  weakMatches: SkillGapItem[];
  impliedMatches: SkillGapItem[];
  missingSkills: SkillGapItem[];
  weakBullets: WeakBullet[];
  /** Skills from the resume, reordered so JD-required terms come first */
  skillsOrderedByRelevance: string[];
  /** 0-100 — % of required JD skills covered (strong + weak + implied) */
  coverageScore: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SOFT_SKILL_KEYWORDS = new Set([
  'leadership', 'communication', 'collaboration', 'teamwork', 'problem-solving',
  'problem solving', 'analytical', 'critical thinking', 'adaptability', 'creativity',
  'empathy', 'mentoring', 'coaching', 'stakeholder management', 'presentation',
  'negotiation', 'time management', 'attention to detail', 'cross-functional',
  'strategic thinking', 'decision making', 'influencing', 'conflict resolution',
  'emotional intelligence', 'ownership', 'accountability', 'proactive', 'initiative',
]);

const SENIORITY_PATTERNS: Array<[RegExp, JDStructure['seniority']]> = [
  [/\b(vp|vice president|c-level|cto|cio|cpo|chief)\b/i, 'Executive'],
  [/\b(director|head of|senior director|principal)\b/i, 'Executive'],
  [/\b(senior|sr\.?|lead|staff|architect)\b/i, 'Senior'],
  [/\b(mid.?level|intermediate|associate)\b/i, 'Mid-Level'],
  [/\b(junior|jr\.?|entry.?level|graduate|intern|trainee)\b/i, 'Junior'],
];

/** Patterns that indicate preferred / nice-to-have skills in JD text */
const PREFERRED_SECTION_RE = /(?:preferred|nice.to.have|bonus|plus|advantageous|desirable|ideally)[^.]*[:–\-]?\s*([^\n.]+)/gi;

/** Patterns that indicate required skills in JD text */
const REQUIRED_SECTION_RE = /(?:required|must.have|essential|mandatory)[^.]*[:–\-]?\s*([^\n.]+)/gi;

/** Weak opening phrases for bullets */
const WEAK_OPENERS_RE = /^(?:responsible for|worked on|helped|assisted in?|participated in|involved in|contributed to|part of the team|supported|tasked with)\b/i;

/** Bullet that is just a tech dump — 5+ comma-separated items, no verb */
const TECH_DUMP_RE = /^[\w\s./]+(?:,\s*[\w\s./]+){4,}$/;

/** Semantic equivalents: key is JD term, values are resume terms that imply it */
const SEMANTIC_EQUIVALENTS: Record<string, string[]> = {
  'microservices': ['distributed systems', 'service-oriented', 'soa', 'containerization', 'api gateway'],
  'machine learning': ['ml', 'predictive model', 'data science', 'statistical model', 'model training'],
  'cloud native': ['aws', 'azure', 'gcp', 'kubernetes', 'serverless', 'lambda', 'cloud'],
  'devops': ['ci/cd', 'pipeline', 'jenkins', 'github actions', 'argocd', 'terraform', 'ansible'],
  'agile': ['scrum', 'kanban', 'sprint', 'jira', 'retro', 'standup'],
  'data-driven': ['metrics', 'kpis', 'analytics', 'dashboard', 'reporting', 'sql', 'data analysis'],
  'stakeholder management': ['stakeholder', 'executive', 'cross-functional', 'presentation'],
  'backend development': ['api', 'rest', 'server-side', 'java', 'python', 'node', 'database', 'sql'],
  'frontend development': ['react', 'angular', 'vue', 'javascript', 'typescript', 'ui', 'html', 'css'],
  'ai innovation': ['llm', 'rag', 'langchain', 'openai', 'generative ai', 'ai', 'ml pipeline'],
  'code reviews': ['review', 'pull request', 'pr', 'peer review', 'code quality', 'best practices'],
  'architectural approaches': ['architecture', 'design pattern', 'system design', 'architect', 'technical design'],
  'emerging technologies': ['llm', 'generative ai', 'blockchain', 'iot', 'edge computing'],
};

// ─── JD Structure Extraction ──────────────────────────────────────────────────

/**
 * Extracts a structured representation of the job description.
 * Uses existing parsed fields from jobData and scans raw description for more.
 */
export function extractJDStructure(jobData: JobData): JDStructure {
  const desc = (jobData.description || '').toLowerCase();
  const title = (jobData.title || '').trim();
  const company = (jobData.company || '').trim();

  // 1. Required skills: start from already-extracted jobData.skills
  const requiredSkills: string[] = [];
  const preferredSkills: string[] = [];

  (jobData.skills || []).forEach(s => requiredSkills.push(s.trim()));
  (jobData.requirements || []).forEach(r => {
    const m = r.match(/^[-•*]?\s*(.+)$/);
    if (m) requiredSkills.push(m[1].trim());
  });

  // 2. Scan raw description for preferred / required sections
  let m: RegExpExecArray | null;
  const re1 = new RegExp(PREFERRED_SECTION_RE.source, 'gi');
  while ((m = re1.exec(desc)) !== null) {
    m[1].split(/[,;]/).map(s => s.trim()).filter(s => s.length > 2 && s.length < 60)
      .forEach(s => preferredSkills.push(s));
  }
  const re2 = new RegExp(REQUIRED_SECTION_RE.source, 'gi');
  while ((m = re2.exec(desc)) !== null) {
    m[1].split(/[,;]/).map(s => s.trim()).filter(s => s.length > 2 && s.length < 60)
      .forEach(s => requiredSkills.push(s));
  }

  // 3. Soft skills: scan entire description
  const softSkills: string[] = [];
  SOFT_SKILL_KEYWORDS.forEach(kw => {
    if (desc.includes(kw)) softSkills.push(kw);
  });

  // 4. Responsibilities: bullet lines that start with an action verb
  const responsibilities: string[] = [];
  const ACTION_VERB_RE = /^[•\-*]\s*([A-Z][a-z]+\s.{10,80})$/m;
  const rawDesc = (jobData.description || '');
  rawDesc.split('\n').forEach(line => {
    const t = line.trim();
    if (ACTION_VERB_RE.test(`• ${t}`) && t.length > 15 && t.length < 120) {
      responsibilities.push(t.replace(/^[•\-*]\s*/, ''));
    }
  });

  // 5. Domain keywords: capitalized multi-word terms that appear in the JD
  const domainKeywords: string[] = [];
  const kwMatches = rawDesc.matchAll(/\b([A-Z][a-zA-Z]+(?: [A-Z][a-zA-Z]+)+)\b/g);
  const seen = new Set<string>();
  for (const km of kwMatches) {
    const kw = km[1];
    if (!seen.has(kw) && kw.length < 40) {
      domainKeywords.push(kw);
      seen.add(kw);
    }
  }

  // 6. Tools: common tool patterns in JD
  const toolPattern = /\b(AWS|Azure|GCP|Docker|Kubernetes|Jenkins|Jira|Confluence|GitHub|GitLab|Terraform|Ansible|Kafka|Redis|PostgreSQL|MySQL|MongoDB|Elasticsearch|Prometheus|Grafana|ArgoCD|Datadog)\b/gi;
  const tools: string[] = [];
  const toolSeen = new Set<string>();
  for (const tm of rawDesc.matchAll(new RegExp(toolPattern.source, 'gi'))) {
    const t = tm[1];
    if (!toolSeen.has(t.toLowerCase())) {
      tools.push(t);
      toolSeen.add(t.toLowerCase());
    }
  }

  // 7. Seniority
  let seniority: JDStructure['seniority'] = 'Unknown';
  for (const [re, level] of SENIORITY_PATTERNS) {
    if (re.test(title) || re.test(desc)) {
      seniority = level;
      break;
    }
  }

  // Deduplicate
  return {
    title,
    company,
    requiredSkills: dedupe(requiredSkills).slice(0, 20),
    preferredSkills: dedupe(preferredSkills).slice(0, 10),
    responsibilities: responsibilities.slice(0, 10),
    domainKeywords: domainKeywords.slice(0, 15),
    softSkills: dedupe(softSkills).slice(0, 8),
    tools: dedupe(tools).slice(0, 10),
    seniority,
  };
}

// ─── Gap Analysis ─────────────────────────────────────────────────────────────

/**
 * Compares the structured JD against the plain-text resume and returns a
 * four-bucket gap analysis plus weak bullet detection and skills ordering.
 */
export function analyzeGaps(resumeText: string, jd: JDStructure): GapAnalysis {
  const resumeLower = resumeText.toLowerCase();

  // Split resume into zones
  const zones = splitResumeIntoZones(resumeText);

  const strongMatches: SkillGapItem[] = [];
  const weakMatches: SkillGapItem[] = [];
  const impliedMatches: SkillGapItem[] = [];
  const missingSkills: SkillGapItem[] = [];

  for (const skill of jd.requiredSkills) {
    const skillLower = skill.toLowerCase().trim();
    if (!skillLower) continue;

    // 1. Strong: skill term appears in a work experience bullet with context
    const expContext = findInZone(skillLower, zones.experience);
    if (expContext && isDemonstratedInContext(expContext)) {
      strongMatches.push({ skill, bucket: 'strong', context: expContext });
      continue;
    }

    // 2. Weak: skill present but only in Skills section flat list (not demonstrated)
    const skillsContext = findInZone(skillLower, zones.skills);
    if (skillsContext || expContext) {
      weakMatches.push({ skill, bucket: 'weak', context: skillsContext || expContext });
      continue;
    }

    // 3. Implied: semantic equivalent present somewhere in resume
    const semanticContext = findSemanticEquivalent(skillLower, resumeLower, resumeText);
    if (semanticContext) {
      impliedMatches.push({ skill, bucket: 'implied', context: semanticContext });
      continue;
    }

    // 4. Missing
    missingSkills.push({ skill, bucket: 'missing' });
  }

  // Weak bullet detection (scan experience zone)
  const weakBullets = detectWeakBullets(zones.experience, jd);

  // Skills ordering: extract skills from resume, put JD matches first
  const skillsOrderedByRelevance = reorderSkills(zones.skills, jd);

  const covered = strongMatches.length + weakMatches.length + impliedMatches.length;
  const total = jd.requiredSkills.length || 1;
  const coverageScore = Math.round((covered / total) * 100);

  return {
    strongMatches,
    weakMatches,
    impliedMatches,
    missingSkills,
    weakBullets,
    skillsOrderedByRelevance,
    coverageScore,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dedupe(arr: string[]): string[] {
  const seen = new Set<string>();
  return arr.filter(s => {
    const k = s.toLowerCase().trim();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

interface ResumeZones {
  contact: string;
  summary: string;
  experience: string;
  skills: string;
  education: string;
  other: string;
}

function splitResumeIntoZones(text: string): ResumeZones {
  const zones: ResumeZones = { contact: '', summary: '', experience: '', skills: '', education: '', other: '' };
  const EXP_RE = /\b(?:work experience|experience|employment|professional experience)\b/i;
  const SKILLS_RE = /\b(?:skills|technical skills|core competencies|proficiencies)\b/i;
  const EDU_RE = /\b(?:education|academic|qualifications)\b/i;
  const SUM_RE = /\b(?:summary|professional summary|profile|objective)\b/i;

  type ZoneKey = keyof ResumeZones;
  let currentZone: ZoneKey = 'contact';
  const lines = text.split('\n');
  for (const line of lines) {
    const t = line.trim();
    if (!t) continue;
    if (EXP_RE.test(t) && t.length < 40)       { currentZone = 'experience'; continue; }
    if (SKILLS_RE.test(t) && t.length < 40)     { currentZone = 'skills'; continue; }
    if (EDU_RE.test(t) && t.length < 40)        { currentZone = 'education'; continue; }
    if (SUM_RE.test(t) && t.length < 40)        { currentZone = 'summary'; continue; }
    zones[currentZone] += line + '\n';
  }
  return zones;
}

function findInZone(skillLower: string, zoneText: string): string | undefined {
  if (!zoneText) return undefined;
  const lines = zoneText.split('\n');
  for (const line of lines) {
    if (line.toLowerCase().includes(skillLower)) {
      return line.trim().slice(0, 140);
    }
  }
  return undefined;
}

/** True if the context line contains a verb + the skill, suggesting it was demonstrated */
function isDemonstratedInContext(context: string): boolean {
  const lower = context.toLowerCase();
  // Has an action verb (not just a tech dump) and some surrounding context
  const hasVerb = /\b(led|built|designed|developed|deployed|managed|delivered|architected|improved|automated|implemented|created|launched|optimized|reduced|increased|scaled|drove|spearheaded|established|mentored|owned|streamlined)\b/i.test(lower);
  const itemCount = (lower.match(/,/g) || []).length;
  return hasVerb && itemCount < 5; // Not a tech dump
}

function findSemanticEquivalent(skillLower: string, resumeLower: string, resumeText: string): string | undefined {
  const synonyms = SEMANTIC_EQUIVALENTS[skillLower];
  if (!synonyms) {
    // Check reverse: if skill appears as a synonym of another key
    for (const [, syns] of Object.entries(SEMANTIC_EQUIVALENTS)) {
      if (syns.some(s => skillLower.includes(s) || s.includes(skillLower))) {
        return findSnippetContaining(syns, resumeLower, resumeText);
      }
    }
    return undefined;
  }
  return findSnippetContaining(synonyms, resumeLower, resumeText);
}

function findSnippetContaining(terms: string[], resumeLower: string, resumeText: string): string | undefined {
  for (const term of terms) {
    const idx = resumeLower.indexOf(term);
    if (idx >= 0) {
      const lineStart = resumeText.lastIndexOf('\n', idx) + 1;
      const lineEnd = resumeText.indexOf('\n', idx);
      return resumeText.slice(lineStart, lineEnd < 0 ? lineStart + 120 : lineEnd).trim().slice(0, 140);
    }
  }
  return undefined;
}

function detectWeakBullets(experienceZone: string, jd: JDStructure): WeakBullet[] {
  const weak: WeakBullet[] = [];
  const bullets = experienceZone.split('\n').filter(l => /^[•\-*]\s/.test(l.trim()) || /^•/.test(l.trim()));

  for (const bullet of bullets) {
    const text = bullet.trim().replace(/^[•\-*]\s*/, '');
    if (text.length < 20) continue;

    const reasons: string[] = [];

    if (WEAK_OPENERS_RE.test(text)) {
      reasons.push('Starts with a weak passive phrase instead of a strong action verb');
    }

    const commaCount = (text.match(/,/g) || []).length;
    if (commaCount >= 4 && !/\b(increased|reduced|improved|delivered|launched|saved|grew)\b/i.test(text)) {
      reasons.push('Appears to be a technology list with no action, context, or outcome');
    }

    const hasOutcome = /\b(result|achiev|improv|reduc|increas|grow|deliver|launch|sav|drove|led to|enabled)\b/i.test(text);
    const hasAction = /\b(led|built|design|develop|deploy|manag|deliver|architect|automat|implement|creat|optim|scale|drove)\b/i.test(text);
    if (hasAction && !hasOutcome && text.length < 90) {
      reasons.push('Good action verb but missing outcome or impact statement');
    }

    if (reasons.length > 0) {
      // Which JD requirements relate to this bullet?
      const related = jd.requiredSkills.filter(skill =>
        text.toLowerCase().includes(skill.toLowerCase().split(' ')[0])
      ).slice(0, 3);
      weak.push({ text, reason: reasons.join('; '), relatedJDRequirements: related });
    }
  }

  return weak.slice(0, 8); // Cap at 8 to keep prompt size reasonable
}

function reorderSkills(skillsZone: string, jd: JDStructure): string[] {
  // Extract all skill tokens from the Skills zone
  const allSkillTokens = skillsZone
    .split(/[\n,•\-*:;]/)
    .map(t => t.trim())
    .filter(t => t.length > 1 && t.length < 50 && !/^\d{4}/.test(t));

  const jdSkillsLower = new Set(
    [...jd.requiredSkills, ...jd.preferredSkills].map(s => s.toLowerCase())
  );

  const jdFirst: string[] = [];
  const preferredMiddle: string[] = [];
  const rest: string[] = [];

  for (const token of allSkillTokens) {
    const lower = token.toLowerCase();
    if (jd.requiredSkills.some(s => lower.includes(s.toLowerCase()) || s.toLowerCase().includes(lower))) {
      jdFirst.push(token);
    } else if (jd.preferredSkills.some(s => lower.includes(s.toLowerCase()) || s.toLowerCase().includes(lower))) {
      preferredMiddle.push(token);
    } else {
      rest.push(token);
    }
  }

  // Deduplicate and merge
  const seen = new Set<string>();
  return [...jdFirst, ...preferredMiddle, ...rest].filter(t => {
    const k = t.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  void jdSkillsLower; // suppress unused warning
}

// ─── Prompt Serializer ────────────────────────────────────────────────────────

/**
 * Converts a GapAnalysis result into a compact, LLM-readable text block
 * that can be injected directly into the optimization prompt.
 */
export function formatGapAnalysisForPrompt(jd: JDStructure, gap: GapAnalysis): string {
  const lines: string[] = [];

  lines.push('=== STRATEGIC ALIGNMENT INSTRUCTIONS (GAP ANALYSIS) ===');
  lines.push(`Target role: ${jd.title || 'Not specified'}${jd.company ? ` at ${jd.company}` : ''}`);
  lines.push(`JD coverage before optimization: ${gap.coverageScore}% of required skills present`);
  lines.push('');

  // Strong matches — leave them alone
  if (gap.strongMatches.length > 0) {
    lines.push('✅ STRONG MATCHES — well-demonstrated in work experience. DO NOT rewrite these sections:');
    gap.strongMatches.slice(0, 6).forEach(s => lines.push(`  • ${s.skill}`));
    lines.push('');
  }

  // Weak matches — same experience, stronger language
  if (gap.weakMatches.length > 0) {
    lines.push('⚠️  WEAK MATCHES — present but under-demonstrated. IMPROVE these (same facts, stronger framing):');
    gap.weakMatches.slice(0, 8).forEach(s => {
      lines.push(`  • ${s.skill}${s.context ? ` (currently: "${s.context.slice(0, 70)}...")` : ''}`);
    });
    lines.push('  → In the Skills section, move these to the top.');
    lines.push('  → In Experience bullets, reframe any line that mentions these to lead with the skill in action.');
    lines.push('');
  }

  // Implied — make explicit
  if (gap.impliedMatches.length > 0) {
    lines.push('💡 IMPLIED SKILLS — experience implies these but they are not explicitly stated. SURFACE them:');
    gap.impliedMatches.slice(0, 5).forEach(s => {
      lines.push(`  • ${s.skill}${s.context ? ` (resume has: "${s.context.slice(0, 60)}...")` : ''}`);
    });
    lines.push('  → Naturally incorporate the exact term into the relevant bullet or summary where it is truthful.');
    lines.push('');
  }

  // Missing — do not invent
  if (gap.missingSkills.length > 0) {
    lines.push('❌ MISSING SKILLS — NOT in the candidate\'s resume. DO NOT invent or imply these:');
    gap.missingSkills.slice(0, 5).forEach(s => lines.push(`  • ${s.skill}`));
    lines.push('');
  }

  // Weak bullets — targeted rewriting targets
  if (gap.weakBullets.length > 0) {
    lines.push('🔧 WEAK BULLETS TO REWRITE (priority targets — same facts, stronger language):');
    gap.weakBullets.slice(0, 5).forEach((wb, i) => {
      lines.push(`  ${i + 1}. "${wb.text.slice(0, 100)}"`);
      lines.push(`     Reason: ${wb.reason}`);
      if (wb.relatedJDRequirements.length > 0) {
        lines.push(`     Align to JD requirement: ${wb.relatedJDRequirements.join(', ')}`);
      }
    });
    lines.push('');
  }

  // Skills ordering
  if (gap.skillsOrderedByRelevance.length > 0) {
    lines.push('📊 SKILLS ORDERING — present in this order (JD-required first):');
    const ordered = gap.skillsOrderedByRelevance.slice(0, 20).join(', ');
    lines.push(`  ${ordered}`);
    lines.push('  → Reorder the Skills section so JD-required terms appear first, then preferred, then remaining.');
    lines.push('');
  }

  lines.push('OPTIMIZATION CONSTRAINT: Only modify what the gap analysis identifies above.');
  lines.push('Sections not mentioned here are already strong — preserve them as-is.');
  lines.push('=== END GAP ANALYSIS ===');

  return lines.join('\n');
}
